"use client";

/**
 * AdminImageUpload — 이미지 업로드 (클릭/드래그&드롭 + 미리보기 + 확대 + 삭제)
 *
 * 제어 컴포넌트: value(이미지 URL 배열) + onChange 로 관리한다.
 * - 기본은 파일을 data URL 로 읽어 value 에 넣는다(미리보기 = 값).
 * - onUpload 를 주면 그 결과 URL(서버 업로드 등)을 value 에 넣는다.
 * - 썸네일을 클릭하면 원본 크기로 확대해서 볼 수 있다 (preview=false 로 끌 수 있음).
 *
 * @example
 * const [images, setImages] = useState<string[]>([]);
 * <AdminImageUpload value={images} onChange={setImages} maxCount={3} />
 *
 * @example // 서버 업로드
 * <AdminImageUpload
 *   value={images}
 *   onChange={setImages}
 *   onUpload={async (file) => (await api.upload(file)).url}
 * />
 *
 * @example // 저장 시점에 업로드 (onUpload 를 안 주면 미리보기만 하고 서버로는 안 보낸다)
 * const pendingFile = useRef<File | null>(null);
 * <AdminImageUpload
 *   value={images}
 *   onChange={setImages}
 *   onFilesAdded={(files) => { pendingFile.current = files[0] ?? null; }}
 * />
 * // 저장 버튼에서: pendingFile.current 가 있으면 그때 업로드해서 실제 URL 로 바꿔치기
 */
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ImagePlus, Loader2, X, ZoomIn } from "lucide-react";

import { cn } from "../../lib/utils";

export interface AdminImageUploadProps {
  /** 이미지 URL 배열 */
  value?: string[];
  onChange?: (urls: string[]) => void;
  /** 최대 개수 (기본 1) */
  maxCount?: number;
  /** accept 속성 (기본 "image/*") */
  accept?: string;
  disabled?: boolean;
  /** 업로드 처리(예: 서버 업로드). 주면 반환 URL 을 value 에 넣는다. 없으면 data URL */
  onUpload?: (file: File) => Promise<string>;
  /**
   * 파일이 선택/드롭된 직후(업로드 전) 원본 File 목록을 받는다.
   * onUpload 없이 미리보기만 해두고, 저장 시점에 이 File 을 직접 업로드하는 흐름에 사용한다.
   */
  onFilesAdded?: (files: File[]) => void;
  /** 썸네일 한 변 크기 px (기본 96) */
  size?: number;
  /** 썸네일 클릭 시 확대(라이트박스)로 보기 (기본 true) */
  preview?: boolean;
  className?: string;
}

/** ref 로 노출되는 명령형 핸들 — 외부에서 강제로 포커스를 트리거할 때 사용 */
export interface AdminImageUploadHandle {
  focus: () => void;
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** file 이 accept 패턴(예: "image/*,.pdf") 중 하나라도 맞는지 검사 */
function matchesAccept(file: File, accept: string): boolean {
  const patterns = accept
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (patterns.length === 0) return true;
  return patterns.some((p) => {
    if (p.endsWith("/*")) return file.type.startsWith(p.slice(0, -1));
    if (p.startsWith(".")) return file.name.toLowerCase().endsWith(p.toLowerCase());
    return file.type === p;
  });
}

export const AdminImageUpload = forwardRef<
  AdminImageUploadHandle,
  AdminImageUploadProps
>(function AdminImageUpload(
  {
    value = [],
    onChange,
    maxCount = 1,
    accept = "image/*",
    disabled,
    onUpload,
    onFilesAdded,
    size = 96,
    preview = true,
    className,
  },
  ref
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);

  useImperativeHandle(ref, () => ({
    focus: () => addButtonRef.current?.focus(),
  }));

  const canAdd = !disabled && value.length + uploading < maxCount;

  const addFiles = async (fileList: FileList | null) => {
    if (!fileList || disabled) return;
    const remaining = maxCount - value.length;
    const files = Array.from(fileList)
      .filter((f) => matchesAccept(f, accept))
      .slice(0, Math.max(0, remaining));
    if (files.length === 0) return;

    onFilesAdded?.(files);

    setUploading((n) => n + files.length);
    try {
      const urls = await Promise.all(
        files.map((f) => (onUpload ? onUpload(f) : readAsDataURL(f)))
      );
      onChange?.([...value, ...urls]);
    } finally {
      setUploading((n) => Math.max(0, n - files.length));
    }
  };

  const removeAt = (index: number) =>
    onChange?.(value.filter((_, i) => i !== index));

  const tileStyle = { width: size, height: size };

  return (
    <div className={cn("flex flex-wrap gap-3", className)}>
      {/* 미리보기 타일 */}
      {value.map((url, i) => (
        <div
          key={i}
          style={tileStyle}
          className="group relative overflow-hidden rounded-lg border bg-muted"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={url}
            alt=""
            onClick={() => preview && setZoomIndex(i)}
            className={cn("size-full object-cover", preview && "cursor-zoom-in")}
          />
          {preview && (
            <span className="pointer-events-none absolute inset-0 hidden items-center justify-center bg-black/30 text-white group-hover:flex">
              <ZoomIn className="size-5" />
            </span>
          )}
          {!disabled && (
            <button
              type="button"
              aria-label="삭제"
              onClick={() => removeAt(i)}
              className="absolute top-1 right-1 rounded-full bg-black/55 p-1 text-white opacity-0 transition-opacity hover:bg-black/75 group-hover:opacity-100"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      ))}

      {/* 업로드 중 타일 */}
      {Array.from({ length: uploading }).map((_, i) => (
        <div
          key={`u-${i}`}
          style={tileStyle}
          className="flex items-center justify-center rounded-lg border bg-muted text-muted-foreground"
        >
          <Loader2 className="size-5 animate-spin" />
        </div>
      ))}

      {/* 추가(업로드) 타일 */}
      {canAdd && (
        <button
          ref={addButtonRef}
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            void addFiles(e.dataTransfer.files);
          }}
          data-drag={dragOver}
          style={tileStyle}
          className={cn(
            "flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-input text-muted-foreground transition-colors",
            "hover:border-ring hover:text-foreground",
            "data-[drag=true]:border-ring data-[drag=true]:bg-accent data-[drag=true]:text-foreground"
          )}
        >
          <ImagePlus className="size-5" />
          <span className="text-xs">업로드</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={maxCount > 1}
        className="hidden"
        onChange={(e) => {
          void addFiles(e.target.files);
          e.target.value = ""; // 같은 파일 재선택 가능하도록 초기화
        }}
      />

      {/* 확대(라이트박스) */}
      <DialogPrimitive.Root
        open={zoomIndex !== null}
        onOpenChange={(o) => !o && setZoomIndex(null)}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            onClick={() => setZoomIndex(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 outline-none"
          >
            <DialogPrimitive.Title className="sr-only">
              이미지 확대 보기
            </DialogPrimitive.Title>
            {zoomIndex !== null && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={value[zoomIndex]}
                alt=""
                onClick={(e) => e.stopPropagation()}
                className="max-h-[90vh] max-w-[90vw] object-contain data-[state=open]:animate-in data-[state=open]:zoom-in-95"
              />
            )}
            <DialogPrimitive.Close className="absolute top-4 right-4 rounded-full bg-black/50 p-2 text-white outline-none transition-colors hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-white">
              <X className="size-5" />
              <span className="sr-only">닫기</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
});
